import React, { useEffect, useState } from 'react';
import { CheckIcon, GripVerticalIcon, PlusIcon, RefreshCwIcon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { createStage, setInitialStage, updateOrganization, updateStage } from '../../services/organizationService';
import { updateProfile } from '../../services/authService';
import { SECTOR_OPTIONS } from '../../data/sectors';
import { FONT_OPTIONS } from '../../data/fonts';
import { Organization } from '../../types';
import { PageHeader } from '../../components/crm/PageHeader';
import { AdminSection } from '../../components/settings/AdminSection';
import { BrandLogo } from '../../components/shared/BrandLogo';
import { LogoUploader } from '../../components/shared/LogoUploader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { toast } from '../../components/ui/Toast';
import { ensureDefaultContent } from '../../services/repair';
import { refresh } from '../../services/store';
export function Settings() {
  const {
    organization,
    isSuperAdmin,
    user,
    role
  } = useAuth();
  const [profile, setProfile] = useState({
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const {
    stages
  } = useWorkspace();
  const [draft, setDraft] = useState<Organization | null>(organization);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newStage, setNewStage] = useState('');
  const [syncing, setSyncing] = useState(false);
  useEffect(() => {
    if (organization && (!draft || draft.id !== organization.id)) setDraft(organization);
  }, [organization, draft]);
  useEffect(() => {
    if (user) setProfile({
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? ''
    });
  }, [user?.id]);
  if (!draft) return null;
  function update(partial: Partial<Organization>) {
    setDraft(prev => prev ? {
      ...prev,
      ...partial
    } : prev);
    setSaved(false);
  }
  /** Guarda el nombre del usuario que ha iniciado sesión. */
  async function handleSaveProfile() {
    if (!user) return;
    setSavingProfile(true);
    try {
      await updateProfile(user.id, profile.firstName, profile.lastName);
      toast.success('Datos personales guardados');
    } catch (err) {
      toast.error('No se han podido guardar', {
        description: err instanceof Error ? err.message : 'Error desconocido.'
      });
    } finally {
      setSavingProfile(false);
    }
  }
  async function handleSave() {
    setSaving(true);
    await updateOrganization(draft!.id, draft!);
    setSaving(false);
    setSaved(true);
  }
  /**
   * Sincroniza el contenido de esta organización con las plantillas oficiales:
   * completa el formulario predeterminado y las landings con lo que falte, sin
   * sobrescribir nada de lo personalizado. Es idempotente.
   */
  async function handleSync() {
    setSyncing(true);
    try {
      const result = await ensureDefaultContent(draft!.id, draft!.name);
      await refresh();
      toast.success('Proyecto actualizado', {
        description: result.addedQuestions > 0 ? `Se han añadido ${result.addedQuestions} preguntas que faltaban.` : 'Tus formularios y landings ya estaban al día.'
      });
    } catch (err) {
      toast.error('No se ha podido actualizar el proyecto', {
        description: err instanceof Error ? err.message : 'Error desconocido.'
      });
    } finally {
      setSyncing(false);
    }
  }
  return <>
      <PageHeader title="Configuración" description="Datos, identidad visual y preferencias de tu empresa." actions={<>
            {saved && <span className="flex items-center gap-1.5 self-center text-sm text-green-600" data-id="element-1557" data-mp="3jmp24ibvb8ad">
                <CheckIcon className="h-4 w-4" data-id="element-1558" data-mp="3noab3sla72fa" /> Guardado
              </span>}
            <Button variant="secondary" leftIcon={<RefreshCwIcon className="h-4 w-4" data-id="element-1560" data-mp="39p0x8zkjskeh" />} onClick={handleSync} loading={syncing} data-id="element-1559" data-mp="3nm6abjioijj8">
              Actualizar proyecto
            </Button>
            <Button onClick={handleSave} loading={saving} data-id="element-1561" data-mp="3em16fjcp239t">
              Guardar cambios
            </Button>
          </>} data-id="element-1556" data-mp="ut0pwkrgdkfr" />

      <Tabs defaultValue="empresa" data-id="element-1562" data-mp="2zwv77vduwd1x">
        <TabsList data-id="element-1563" data-mp="34pob6h1i3hcy">
          <TabsTrigger value="cuenta" data-id="element-1564" data-mp="34q7uo26sudt3">Mi cuenta</TabsTrigger>
          <TabsTrigger value="empresa" data-id="element-1565" data-mp="2qy9qm5ry383s">Empresa</TabsTrigger>
          <TabsTrigger value="identidad" data-id="element-1566" data-mp="2qzul5ve9er1n">Branding</TabsTrigger>
          <TabsTrigger value="pipeline" data-id="element-1567" data-mp="2vwb6yixzktbk">Embudo de ventas</TabsTrigger>
          <TabsTrigger value="notificaciones" data-id="element-1568" data-mp="2gzu30cs4mgz1">Notificaciones</TabsTrigger>
          <TabsTrigger value="privacidad" data-id="element-1569" data-mp="2h1wy3b84z0am">Privacidad</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="administracion" data-id="element-1570" data-mp="2lyexrwom08g3">Administración</TabsTrigger>}
        </TabsList>

        <TabsContent value="cuenta" data-id="element-1571" data-mp="22dn5zpb4re90">
          <div className="space-y-4 rounded-lg border border-border bg-background p-5" data-id="element-1572" data-mp="1ybji2179yh6u">
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1573" data-mp="1ydw871n6rxuz">
              <Input id="u-first" label="Nombre" value={profile.firstName} onChange={e => setProfile(p => ({
              ...p,
              firstName: e.target.value
            }))} data-id="element-1574" data-mp="2cc40uxd6wd0c" />
              <Input id="u-last" label="Apellidos" value={profile.lastName} onChange={e => setProfile(p => ({
              ...p,
              lastName: e.target.value
            }))} data-id="element-1575" data-mp="2lb7o8dje3ekn" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1576" data-mp="2v0r5jergf0de">
              <div data-id="element-1577" data-mp="2q2plorm7ex0v">
                <p className="text-sm font-medium text-foreground" data-id="element-1578" data-mp="2q27itc51dkjz">
                  Correo de la cuenta
                </p>
                <p className="mt-1 break-all text-sm text-muted-foreground" data-id="element-1579" data-mp="350rqt68vvqk8">
                  {user?.email ?? '—'}
                </p>
                <p className="mt-1 text-xs text-muted-foreground" data-id="element-1580" data-mp="2zysq644j37qz">
                  Es el correo con el que inicias sesión.
                </p>
              </div>
              <div data-id="element-1581" data-mp="3c3yby2rfgkwa">
                <p className="text-sm font-medium text-foreground" data-id="element-1582" data-mp="3c3g4e9zbek92">
                  Empresa o persona
                </p>
                <p className="mt-1 text-sm text-muted-foreground" data-id="element-1583" data-mp="3lszofiyrkk5i">
                  {organization?.name ?? '—'}
                </p>
                <p className="mt-1 text-xs text-muted-foreground" data-id="element-1584" data-mp="3qsudri2w51og">
                  Rol: {role ?? '—'}
                  {user?.createdAt ? ` · Alta: ${new Date(user.createdAt).toLocaleDateString('es-ES')}` : ''}
                </p>
              </div>
            </div>
            <Button onClick={handleSaveProfile} loading={savingProfile} data-id="element-1585" data-mp="8mg0zmgcyxrn">
              Guardar datos personales
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="empresa" data-id="element-1586" data-mp="25gkle49pgpja">
          <div className="grid gap-4 rounded-lg border border-border bg-background p-5 sm:grid-cols-2" data-id="element-1587" data-mp="2adkr2e2n7q9v">
            <Input id="o-name" label="Nombre de empresa o persona" value={draft.name} onChange={e => update({
            name: e.target.value
          })} data-id="element-1588" data-mp="22utoucic7q7i" />
            <Select id="o-sector" label="Sector" options={SECTOR_OPTIONS} value={draft.sector} onChange={e => update({
            sector: e.target.value
          })} data-id="element-1589" data-mp="1y1ie40aempab" />
            <Input id="o-email" label="Email de contacto" type="email" value={draft.email ?? ''} onChange={e => update({
            email: e.target.value
          })} data-id="element-1590" data-mp="1o6qfqu8zzyeh" />
            <Input id="o-phone" label="Teléfono" value={draft.phone ?? ''} onChange={e => update({
            phone: e.target.value
          })} data-id="element-1591" data-mp="2gtjnvxopvzyi" />
            <Input id="o-web" label="Web" value={draft.website ?? ''} onChange={e => update({
            website: e.target.value
          })} data-id="element-1592" data-mp="2bt5dwa3k2fxu" />
            <Input id="o-address" label="Dirección" value={draft.address ?? ''} onChange={e => update({
            address: e.target.value
          })} data-id="element-1593" data-mp="10gcmx1il9viu" />
            <Select id="o-tz" label="Zona horaria" options={[{
            value: 'Europe/Madrid',
            label: 'Europe/Madrid'
          }, {
            value: 'Atlantic/Canary',
            label: 'Atlantic/Canary'
          }, {
            value: 'America/Mexico_City',
            label: 'America/Mexico_City'
          }, {
            value: 'America/Bogota',
            label: 'America/Bogota'
          }, {
            value: 'America/Argentina/Buenos_Aires',
            label: 'Buenos Aires'
          }]} value={draft.timezone} onChange={e => update({
            timezone: e.target.value
          })} data-id="element-1594" data-mp="viunc1qgosni" />
            <Input id="o-logo" label="Logo (URL)" value={draft.logoUrl ?? ''} onChange={e => update({
            logoUrl: e.target.value
          })} data-id="element-1595" data-mp="1v31p61n5arq1" />
          </div>
        </TabsContent>

        <TabsContent value="identidad" data-id="element-1596" data-mp="1z2am1fdhf5pt">
          <div className="space-y-6 rounded-2xl border border-border bg-background p-6 sm:p-8" data-id="element-1597" data-mp="2mt6iwqwsor3x">
            <p className="text-sm text-muted-foreground" data-id="element-1598" data-mp="2rl7u09i88bsd">
              Este logotipo y estos colores se aplican automáticamente en el
              dashboard, el menú lateral, tus landings, tus formularios, el pie de
              página, la página de gracias y los emails.
            </p>

            <LogoUploader organizationId={draft.id} kind="logo" value={draft.brand.logoUrl ?? draft.logoUrl} onChange={value => update({
            logoUrl: value,
            brand: {
              ...draft.brand,
              logoUrl: value
            }
          })} data-id="element-1599" data-mp="pw1u55ouulls" />

            <Input id="b-name" label="Nombre de la empresa" value={draft.name} onChange={e => update({
            name: e.target.value
          })} data-id="element-1600" data-mp="3odfn1rjdb5y3" />

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1601" data-mp="76vl6adv58d6">
              <Input id="b-primary" label="Color principal" type="color" value={draft.brand.primaryColor} onChange={e => update({
              brand: {
                ...draft.brand,
                primaryColor: e.target.value
              }
            })} data-id="element-1602" data-mp="25y1tuh4a8a3" />
              <Input id="b-secondary" label="Color secundario" type="color" value={draft.brand.secondaryColor} onChange={e => update({
              brand: {
                ...draft.brand,
                secondaryColor: e.target.value
              }
            })} data-id="element-1603" data-mp="jy8bpi6vbtwa" />
              <Input id="b-button" label="Color de botones" type="color" value={draft.brand.buttonColor ?? draft.brand.primaryColor} onChange={e => update({
              brand: {
                ...draft.brand,
                buttonColor: e.target.value
              }
            })} data-id="element-1604" data-mp="a6kfl3zd9ooh" />
              <Input id="b-text" label="Color de textos" type="color" value={draft.brand.textColor ?? '#0B0B0F'} onChange={e => update({
              brand: {
                ...draft.brand,
                textColor: e.target.value
              }
            })} data-id="element-1605" data-mp="3scgn5dh9e418" />
              <Input id="b-bg" label="Color de fondo" type="color" value={draft.brand.backgroundColor ?? '#FFFFFF'} onChange={e => update({
              brand: {
                ...draft.brand,
                backgroundColor: e.target.value
              }
            })} data-id="element-1606" data-mp="1lntwbir422m3" />
              <Input id="b-favicon" label="Favicon (URL)" value={draft.brand.faviconUrl ?? ''} onChange={e => update({
              brand: {
                ...draft.brand,
                faviconUrl: e.target.value
              }
            })} data-id="element-1607" data-mp="1t6tfbnants4x" />
              <Select id="b-font" label="Tipografía principal" options={FONT_OPTIONS} value={draft.brand.fontFamily} onChange={e => update({
              brand: {
                ...draft.brand,
                fontFamily: e.target.value
              }
            })} data-id="element-1608" data-mp="22wcyyv675q0u" />
              <Select id="b-font2" label="Tipografía secundaria" options={FONT_OPTIONS} value={draft.brand.secondaryFontFamily ?? draft.brand.fontFamily} onChange={e => update({
              brand: {
                ...draft.brand,
                secondaryFontFamily: e.target.value
              }
            })} data-id="element-1609" data-mp="1f7239s3peltk" />
              <Input id="b-domain" label="Dominio propio" placeholder="paginas.miempresa.com" value={draft.brand.domain ?? ''} onChange={e => update({
              brand: {
                ...draft.brand,
                domain: e.target.value
              }
            })} data-id="element-1610" data-mp="2vk8qllgwsrqr" />
            </div>

            <div className="rounded-xl border border-border bg-surface p-5" data-id="element-1611" data-mp="14ipcf1j8iflj">
              <p className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground" data-id="element-1612" data-mp="10i5fnz2kpdzx">
                Vista previa
              </p>
              <div className="flex flex-wrap items-center gap-4 rounded-lg px-5 py-4" style={{
              backgroundColor: draft.brand.backgroundColor ?? '#FFFFFF',
              fontFamily: `${draft.brand.fontFamily}, Inter, sans-serif`
            }} data-id="element-1613" data-mp="cqq91u3rkaiv">
                <BrandLogo src={draft.brand.logoUrl ?? draft.logoUrl} size={24} data-id="element-1614" data-mp="gtd3m2rtk9jr" />
                <span className="text-sm font-semibold" style={{
                color: draft.brand.secondaryColor
              }} data-id="element-1615" data-mp="3p9pp6xqpcexf">
                  {draft.name}
                </span>
                <span className="text-sm" style={{
                color: draft.brand.textColor ?? '#5F6673',
                fontFamily: `${draft.brand.secondaryFontFamily ?? draft.brand.fontFamily}, Inter, sans-serif`
              }} data-id="element-1616" data-mp="3b4z6h0kq8niz">
                  Texto de ejemplo
                </span>
                <span className="ml-auto inline-flex h-9 items-center rounded-lg px-4 text-sm font-medium text-white" style={{
                backgroundColor: draft.brand.buttonColor ?? draft.brand.primaryColor
              }} data-id="element-1617" data-mp="365cv6woglhct">
                  Botón principal
                </span>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pipeline" data-id="element-1618" data-mp="3jwrez5q6xmkx">
          <div className="rounded-lg border border-border bg-background p-5" data-id="element-1619" data-mp="3ovu7xrswc2cj">
            <ul className="space-y-2" data-id="element-1620" data-mp="3oxx834nrjbuz">
              {stages.map(stage => <li key={stage.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3" data-id="element-1621" data-mp="28h2s0ecxsnk4">
                  <GripVerticalIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-1622" data-mp="2di8n2iu2kmwt" />
                  <Input id={`s-name-${stage.id}`} value={stage.name} onChange={e => updateStage(stage.id, {
                name: e.target.value
              })} data-id="element-1623" data-mp="2tpej5ktp573p" />
                  <input type="color" aria-label={`Color de ${stage.name}`} value={stage.color} onChange={e => updateStage(stage.id, {
                color: e.target.value
              })} className="h-9 w-12 cursor-pointer rounded-md border border-border bg-background" data-id="element-1624" data-mp="2jzbrpp28417h" />
                  <Switch id={`s-active-${stage.id}`} label="Activo" checked={stage.active} onChange={checked => updateStage(stage.id, {
                active: checked
              })} data-id="element-1625" data-mp="37o4hf3s95ctn" />
                  <Button size="sm" variant={stage.isInitial ? 'primary' : 'ghost'} onClick={() => setInitialStage(stage.organizationId, stage.id)} data-id="element-1626" data-mp="2xpovdf830tr0">
                    {stage.isInitial ? 'Estado inicial' : 'Marcar como inicial'}
                  </Button>
                </li>)}
            </ul>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row" data-id="element-1627" data-mp="1shdtm4ij25a">
              <Input id="new-stage" placeholder="Nombre del nuevo estado" value={newStage} onChange={e => setNewStage(e.target.value)} data-id="element-1628" data-mp="1ual93my9qec" />
              <Button leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1630" data-mp="bq586ay7i7mx" />} disabled={!newStage.trim()} onClick={async () => {
              await createStage(draft.id, newStage.trim(), '#3D8BFF');
              setNewStage('');
            }} data-id="element-1629" data-mp="bqnb1qfdjk3t">
                Añadir estado
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notificaciones" data-id="element-1631" data-mp="pgqt05780rng">
          <div className="space-y-4 rounded-lg border border-border bg-background p-5" data-id="element-1632" data-mp="pjwgxh445nhl">
            {([['newLead', 'Nuevo lead'], ['leadAssigned', 'Lead asignado'], ['statusChange', 'Cambio de estado'], ['newMeeting', 'Nueva reunión']] as const).map(([key, label]) => <Switch key={key} id={`n-${key}`} label={label} checked={draft.notifications[key]} onChange={checked => update({
            notifications: {
              ...draft.notifications,
              [key]: checked
            }
          })} data-id="element-1633" data-mp="2w9t7s6kod760" />)}
          </div>
        </TabsContent>

        <TabsContent value="privacidad" data-id="element-1634" data-mp="2p6cx1nl007os">
          <div className="space-y-4 rounded-lg border border-border bg-background p-5" data-id="element-1635" data-mp="2p7o35u1x1rj2">
            <Input id="p-privacy" label="URL de la política de privacidad" value={draft.privacy.privacyPolicyUrl} onChange={e => update({
            privacy: {
              ...draft.privacy,
              privacyPolicyUrl: e.target.value
            }
          })} data-id="element-1636" data-mp="332zwpy4c5bye" />
            <Input id="p-legal" label="URL del aviso legal" value={draft.privacy.legalNoticeUrl} onChange={e => update({
            privacy: {
              ...draft.privacy,
              legalNoticeUrl: e.target.value
            }
          })} data-id="element-1637" data-mp="383e6bkll4tyj" />
            <Textarea id="p-consent" label="Texto de consentimiento" rows={3} value={draft.privacy.consentText} onChange={e => update({
            privacy: {
              ...draft.privacy,
              consentText: e.target.value
            }
          })} data-id="element-1638" data-mp="3vtrqyq1ubqj3" />
            <Textarea id="p-cookies" label="Texto de cookies" rows={3} value={draft.privacy.cookiesText} onChange={e => update({
            privacy: {
              ...draft.privacy,
              cookiesText: e.target.value
            }
          })} data-id="element-1639" data-mp="ebku9aqot9gk" />
          </div>
        </TabsContent>

        {/* Solo para el super administrador de la plataforma. */}
        {isSuperAdmin && <TabsContent value="administracion" data-id="element-1640" data-mp="3qrevfrypnsd4">
            <AdminSection data-id="element-1641" data-mp="3qqmz2exahqls" />
          </TabsContent>}
      </Tabs>
    </>;
}